import { useState, type FormEvent } from 'react';
import { useNavigate, useLocation, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useBrand, useLogoUrl } from '@/lib/brand';
import { useAuth } from '@/lib/auth';
import { knoxApi, KnoxApiError } from '@/lib/knox-api';
import { AccessApplicationDialog } from '@/components/AccessApplicationDialog';

export function InviteCodeEntryPage({ codeType }: { codeType: 'join' | 'invite' }) {
  const { t } = useTranslation('knox-aiops');
  const navigate = useNavigate();
  const location = useLocation();
  const { user, refreshSession } = useAuth();
  const brand = useBrand();
  const logoUrl = useLogoUrl();

  // Read pre-filled token from router state (set by OrgBindingPage) or localStorage
  const routerToken = (location.state as { token?: string } | null)?.token;
  const lsToken = codeType === 'join'
    ? localStorage.getItem('pending_join_token')
    : localStorage.getItem('pending_invite_token');
  const prefilledToken = routerToken || lsToken || '';

  const [code, setCode] = useState(prefilledToken);
  const [orgName, setOrgName] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [showAccessDialog, setShowAccessDialog] = useState(false);

  const isJoin = codeType === 'join';
  const isLoggedIn = !!user?.user_id;

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setError(null);

    // Logged-in user: call password-free API directly
    if (isLoggedIn) {
      if (isJoin) {
        if (!code.trim()) return;
        setLoading(true);
        try {
          const resp = await knoxApi.joinOrgLoggedIn({ token: code.trim() });
          await refreshSession();
          if (resp.default_workspace_id) {
            navigate(`/workspaces/${resp.default_workspace_id}`, { replace: true });
          } else {
            navigate('/workspaces', { replace: true });
          }
        } catch (e) {
          if (e instanceof KnoxApiError && e.code === 'QUOTA_EXCEEDED') {
            setError(t('joinCode.memberLimitReached'));
          } else {
            setError(t('joinCode.invalidOrExpired'));
          }
        } finally {
          setLoading(false);
        }
        return;
      }

      // invite: create org
      if (!code.trim() || !orgName.trim()) return;
      setLoading(true);
      try {
        const resp = await knoxApi.createOrgLoggedIn({
          token: code.trim(),
          org_name: orgName.trim(),
        });
        await refreshSession();
        if (resp.default_workspace_id) {
          navigate(`/workspaces/${resp.default_workspace_id}`, { replace: true });
        } else {
          navigate('/workspaces', { replace: true });
        }
      } catch (e) {
        if (e instanceof KnoxApiError && e.code === 'QUOTA_EXCEEDED') {
          setError(t('inviteCode.memberLimitReached'));
        } else {
          setError(t('inviteCode.invalidOrExpired'));
        }
      } finally {
        setLoading(false);
      }
      return;
    }

    // Not logged in: validate code, then navigate to registration form
    if (isJoin) {
      if (!code.trim()) return;
      setLoading(true);
      setError(null);
      try {
        await knoxApi.getOrgJoinInfo(code.trim());
        navigate(`/join/${code.trim()}`, { replace: true });
      } catch (e) {
        setError(e instanceof KnoxApiError && e.code === 'QUOTA_EXCEEDED'
          ? t('joinCode.memberLimitReached')
          : t('joinCode.invalidOrExpired'));
      } finally {
        setLoading(false);
      }
      return;
    }

    // invite: validate code, then navigate to /invite/:token
    if (!code.trim() || !orgName.trim()) return;
    setLoading(true);
    setError(null);
    try {
      await knoxApi.getOrgInviteInfo(code.trim());
      navigate(`/invite/${code.trim()}`, { state: { orgName: orgName.trim() }, replace: true });
    } catch (e) {
      setError(e instanceof KnoxApiError && e.code === 'QUOTA_EXCEEDED'
        ? t('inviteCode.memberLimitReached')
        : t('inviteCode.invalidOrExpired'));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="flex h-screen flex-col bg-white font-[Inter,sans-serif] text-foreground">
      <header className="flex shrink-0 w-full items-center justify-between px-6 py-3 bg-white/80 backdrop-blur-md border-b border-black/[0.04]">
        <div className="flex items-center gap-2">
          <img src={logoUrl} alt={brand} className="h-7 w-auto" />
          <span className="font-heading text-xl font-extrabold tracking-tighter text-foreground">{brand}</span>
        </div>
      </header>

      <main className="relative flex flex-1 min-h-0 flex-col items-center justify-center overflow-auto px-4 py-6">
        <div className="knox-dot-grid pointer-events-none absolute inset-0 opacity-10" />
        <div className="knox-card-glass relative w-full max-w-[420px] p-6 sm:p-8">
          <div className="mb-6 sm:mb-8 text-center">
            <div className="mb-3 flex justify-center">
              <img src={logoUrl} alt="" className="h-10 sm:h-12 w-auto" />
            </div>
            <h1 className="font-heading text-2xl font-extrabold tracking-tight text-foreground mb-1">
              {t(isJoin ? 'joinCode.title' : 'inviteCode.title')}
            </h1>
            <p className="text-sm text-muted-foreground">{t(isJoin ? 'joinCode.subtitle' : 'inviteCode.subtitle')}</p>
          </div>

          <form id="invite-code-form" onSubmit={handleSubmit} className="space-y-4">
            <div className="space-y-2">
              <label htmlFor="invite-code" className="px-1 text-[0.6875rem] font-bold uppercase tracking-widest text-muted-foreground">
                {t(isJoin ? 'joinCode.codeLabel' : 'inviteCode.codeLabel')}
              </label>
              <input
                id="invite-code"
                type="text"
                value={code}
                onChange={(e) => setCode(e.target.value)}
                placeholder={t(isJoin ? 'joinCode.placeholder' : 'inviteCode.placeholder')}
                autoFocus
                required
                className="w-full rounded-full border border-black/[0.16] bg-surface-container-lowest py-3 px-5 text-sm text-foreground placeholder:text-muted-foreground/40 focus:border-primary/30 focus:outline-none focus:ring-2 focus:ring-primary/10 transition-all duration-300"
              />
            </div>

            {!isJoin && (
              <div className="space-y-2">
                <label htmlFor="org-name" className="px-1 text-[0.6875rem] font-bold uppercase tracking-widest text-muted-foreground">
                  {t('inviteCode.orgNameLabel')}
                </label>
                <input
                  id="org-name"
                  type="text"
                  value={orgName}
                  onChange={(e) => setOrgName(e.target.value)}
                  placeholder={t('inviteCode.orgNamePlaceholder')}
                  required
                  className="w-full rounded-full border border-black/[0.16] bg-surface-container-lowest py-3 px-5 text-sm text-foreground placeholder:text-muted-foreground/40 focus:border-primary/30 focus:outline-none focus:ring-2 focus:ring-primary/10 transition-all duration-300"
                />
              </div>
            )}

            {error && (
              <p className="text-sm text-destructive px-1">{error}</p>
            )}

            <button
              type="submit"
              disabled={loading || (isJoin ? !code.trim() : !code.trim() || !orgName.trim())}
              className="w-full rounded-full bg-gradient-to-br from-[#0070F3] to-[#005ac6] px-5 py-3 text-sm font-bold text-white shadow-lg transition-all duration-300 hover:shadow-[0_0_20px_rgba(135,173,255,0.3)] active:scale-[0.98] disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {loading ? (
                <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin mx-auto" />
              ) : (
                t(isJoin ? 'joinCode.submit' : 'inviteCode.submit')
              )}
            </button>
          </form>

          {!isJoin && (
            <>
              <div className="mt-5 flex items-center gap-3">
                <div className="flex-1 h-px bg-black/[0.08]" />
                <span className="text-xs text-muted-foreground">{t('inviteCode.noCode')}</span>
                <div className="flex-1 h-px bg-black/[0.08]" />
              </div>

              <button
                type="button"
                onClick={() => setShowAccessDialog(true)}
                className="mt-3 w-full text-center text-sm text-primary hover:text-primary/80 font-medium transition-colors"
              >
                {t('accessApplication.trigger')}
              </button>
            </>
          )}

          <div className="mt-6 text-center">
            <Link to="/org-binding" className="text-sm text-muted-foreground hover:text-foreground transition-colors">
              {t(isJoin ? 'joinCode.back' : 'inviteCode.back')}
            </Link>
          </div>
        </div>
      </main>

      <footer className="shrink-0 w-full bg-white">
        <div className="mx-auto flex w-full max-w-7xl flex-col items-center gap-2 px-8 py-4">
          <span className="text-[0.6875rem] uppercase tracking-widest text-muted-foreground/60">{t('login.copyright')}</span>
          <div className="flex gap-6">
            <a href="/terms" className="text-[0.6875rem] uppercase tracking-widest text-muted-foreground/40 transition-colors hover:text-primary hover:underline underline-offset-4">{t('login.termsOfService')}</a>
            <a href="/privacy" className="text-[0.6875rem] uppercase tracking-widest text-muted-foreground/40 transition-colors hover:text-primary hover:underline underline-offset-4">{t('login.privacyPolicy')}</a>
            <a href="/refund" className="text-[0.6875rem] uppercase tracking-widest text-muted-foreground/40 transition-colors hover:text-primary hover:underline underline-offset-4">{t('login.refundPolicy')}</a>
          </div>
        </div>
      </footer>

      <AccessApplicationDialog
        open={showAccessDialog}
        onClose={() => setShowAccessDialog(false)}
      />
    </div>
  );
}
